// ===================================================================
// MC-107 — Ocultação de dados retroativos da carteira Resiclean
// ===================================================================
//
// 109 geradores da carteira Resiclean foram onboardados no Portal do
// Gerador. Coletas de 2026-01-01 a 2026-07-31 foram lançadas em sistema
// apenas para documentar faturamento retroativo, com resíduo/
// acondicionamento/tratamento que não refletem a operação real. Esses
// geradores não podem ver, em nenhuma tela do portal, nenhum dado
// (coleta, fatura, indicador ambiental/dashboard) referente a esse
// período. A ocultação é só de experiência no portal — não afeta o
// faturamento real nem telas internas fora dele.
//
// Decisão registrada no ticket: por urgência de entrada do gerador, o
// corte é feito no frontend (não no backend), com plano de
// revisitar como configuração de sistema depois.
//
// Lista fechada: carteira Resiclean completa a ocultar (160 ids — lista
// atualizada em 2026-09-08, substitui integralmente a lista original de 16
// geradores confirmados com coleta fictícia no período; o ticket citava 109
// geradores onboardados no total, dos quais só um subconjunto tinha coleta).
export const CUTOFF_GENERATOR_IDS: number[] = [
  1721, 1722, 1723, 1724, 1726, 1730, 1736, 1737,
  1738, 1745, 1748, 1753, 1756, 1757, 1759, 1761,
  1764, 1765, 1771, 1774, 1775, 1776, 1778, 1779,
  1780, 1783, 1797, 1799, 1800, 1801, 1803, 1808,
  1809, 1811, 1812, 1815, 1817, 1818, 1819, 1820,
  1821, 1823,
  // 1853 (URBIA | Parque Ibirapuera) fora da lista: operação real (Multilixo, MTR no SIGOR-SP) em
  // jan–jul, não coleta retroativa de faturamento — a entrada na lista foi indevida (MC-107).
  // 1853,
  1863, 1864, 1865, 1866, 1867,
  1868, 1869, 1870, 1871, 1872, 1873,
  // 1874 (Zadara do Brasil) fora da lista: confirmado com o owner do MC-107 que a entrada foi indevida.
  // 1874,
  1876,
  1877, 1878, 1880, 1881, 1882, 1883, 1884, 1886,
  2689, 2827, 3420, 3421, 3423, 3424, 3426, 3427,
  3428, 3434, 3436, 3437, 3442, 3443, 3445, 3446,
  3447, 3448, 3449, 3450, 3451, 3453, 3454, 3455,
  3458, 3459, 3482, 3489, 3507, 3508, 3537, 3538,
  3539, 3540, 3541, 3542, 3543, 3544, 3545, 3546,
  3547, 3550, 3551, 3553, 3554, 3555, 3556, 3557,
  3558, 3559, 3560, 3561, 3562, 3563, 3564, 3565,
  3597, 3600, 3617, 3618, 3619, 3620, 3643, 3644,
  3648, 3649, 3651, 3652, 3655, 3657, 3663, 3670,
  3671, 3672, 3673, 3674, 3679, 3735, 3739, 4140,
  4141, 4142, 4143, 4144, 4145, 4146, 4234, 4235,
  4236, 4237, 4238, 4239, 4240, 4241, 4242, 4401,
];

export const CUTOFF_PERIOD_START_DATE = '2026-01-01';
export const CUTOFF_PERIOD_END_DATE = '2026-07-31';
export const CUTOFF_PERIOD_START_MONTH = '2026-01';
export const CUTOFF_PERIOD_END_MONTH = '2026-07';

/** Primeiro dia/mês fora do recorte oculto — usado para recortar a cabeça de um intervalo. */
const AFTER_CUTOFF_DATE = '2026-08-01';
const AFTER_CUTOFF_MONTH = '2026-08';

/** Janela vazia determinística: nenhum dado real do portal existe nesse intervalo. */
const EMPTY_WINDOW_DATE = '1900-01-01';
const EMPTY_WINDOW_MONTH = '1900-01';

// ===================================================================
// Predicados
// ===================================================================

export function isCutoffGenerator(id: number | string | null | undefined): boolean {
  if (id == null) return false;
  const numericId = typeof id === 'number' ? id : Number(id);
  if (Number.isNaN(numericId)) return false;
  return CUTOFF_GENERATOR_IDS.includes(numericId);
}

/** Compara uma data 'YYYY-MM-DD' contra o recorte oculto (comparação lexicográfica, formato fixo). */
export function isDateInCutoffRange(date: string | null | undefined): boolean {
  if (!date) return false;
  return date >= CUTOFF_PERIOD_START_DATE && date <= CUTOFF_PERIOD_END_DATE;
}

/** Compara um mês 'YYYY-MM' (billing_period ou meses do Indicadores Ambientais) contra o recorte. */
export function isMonthInCutoffRange(month: string | null | undefined): boolean {
  if (!month) return false;
  // billing_period/months podem vir como 'YYYY-MM-DD' também — normaliza para 'YYYY-MM'.
  const normalized = month.slice(0, 7);
  return normalized >= CUTOFF_PERIOD_START_MONTH && normalized <= CUTOFF_PERIOD_END_MONTH;
}

// ===================================================================
// Resolução de escopo
// ===================================================================

/**
 * Resolve o(s) generator_id efetivo(s) de uma consulta: o filtro explícito
 * escolhido na UI tem precedência (cobre o interno Musa filtrando por um
 * gerador específico); sem filtro, cai para o próprio gerador do usuário
 * logado (cobre o gerador externo vendo sua própria tela). Sem nenhum dos
 * dois, devolve `[]` — escopo indeterminado, quem chama deve tratar como
 * "não recortar" (ver `clampDateRangeForCutoff`).
 */
export function resolveEffectiveGeneratorIds(
  filterGeneratorIds: number[] | null | undefined,
  profileGeneratorId: number | null | undefined,
): number[] {
  if (filterGeneratorIds != null && filterGeneratorIds.length > 0) {
    return filterGeneratorIds;
  }
  if (profileGeneratorId != null) {
    return [profileGeneratorId];
  }
  return [];
}

/**
 * Se o escopo efetivo da consulta pertence inteiro à lista de corte. Usado
 * por seções do Dashboard cuja janela é relativa a hoje e não manda
 * `date_from`/`date_to` ao gateway (por isso `clampDateRangeForCutoff` não
 * se aplica) — a resposta vem com um campo `month` por item, então dá pra
 * excluir os meses do recorte diretamente na resposta.
 */
export function isEntireScopeCutoff(effectiveGeneratorIds: number[]): boolean {
  return effectiveGeneratorIds.length > 0 && effectiveGeneratorIds.every(isCutoffGenerator);
}

// ===================================================================
// Filtros para listagens item a item
// ===================================================================

/**
 * Remove de uma listagem os itens cujo gerador está na lista de corte E cuja
 * data cai no recorte oculto. Usado onde cada registro carrega seu próprio
 * generator_id (Coletas, Sob Demanda, Faturas) — funciona independente de a
 * tela ter aplicado algum filtro de data, ao contrário do recorte de
 * intervalo (`clampDateRangeForCutoff`), que depende de haver um intervalo
 * pedido para ajustar.
 */
export function excludeCutoffItems<T>(
  items: T[],
  getGeneratorId: (item: T) => number | string | null | undefined,
  getDate: (item: T) => string | null | undefined,
  dateGranularity: 'day' | 'month' = 'day',
): T[] {
  const isDateHidden = dateGranularity === 'month' ? isMonthInCutoffRange : isDateInCutoffRange;
  return items.filter((item) => !(isCutoffGenerator(getGeneratorId(item)) && isDateHidden(getDate(item))));
}

// ===================================================================
// Recorte de intervalo (para agregados/calendário, sem item individual)
// ===================================================================

/**
 * Ajusta um intervalo de datas 'YYYY-MM-DD' pedido ao gateway quando TODOS os
 * geradores efetivos da consulta estão na lista de corte: recorta a cabeça
 * do intervalo se ele avança para depois do corte, ou devolve uma janela
 * vazia determinística se o intervalo pedido cai inteiro dentro do recorte.
 *
 * Quando o escopo mistura geradores da lista com outros (ex.: visão
 * combinada de todos os geradores) ou é indeterminado, devolve o intervalo
 * inalterado — não dá pra recortar sem esconder dado legítimo de quem não
 * está na lista.
 */
export function clampDateRangeForCutoff(
  effectiveGeneratorIds: number[],
  dateFrom: string,
  dateTo: string,
): { date_from: string; date_to: string } {
  if (!isEntireScopeCutoff(effectiveGeneratorIds)) {
    return { date_from: dateFrom, date_to: dateTo };
  }

  const overlaps = dateTo >= CUTOFF_PERIOD_START_DATE && dateFrom <= CUTOFF_PERIOD_END_DATE;
  if (!overlaps) {
    return { date_from: dateFrom, date_to: dateTo };
  }

  const fullyInside = dateFrom >= CUTOFF_PERIOD_START_DATE && dateTo <= CUTOFF_PERIOD_END_DATE;
  if (fullyInside) {
    return { date_from: EMPTY_WINDOW_DATE, date_to: EMPTY_WINDOW_DATE };
  }

  // Overlap parcial: caso comum é o intervalo começar dentro/antes do corte e
  // seguir depois dele (ex.: "últimos 12 meses" a partir de hoje). Recorta a
  // cabeça. O caso simétrico (intervalo termina dentro do corte, mas começa
  // antes de 2026-01-01) é tratado do mesmo jeito conservador quando não se
  // reduz limpo a isso — esses geradores só existem no portal a partir deste
  // recorte, então não há dado legítimo antes dele.
  if (dateTo > CUTOFF_PERIOD_END_DATE) {
    return { date_from: AFTER_CUTOFF_DATE, date_to: dateTo };
  }

  return { date_from: EMPTY_WINDOW_DATE, date_to: EMPTY_WINDOW_DATE };
}

/**
 * Remove de uma resposta agregada por mês (`{ month: 'YYYY-MM-DD' | 'YYYY-MM', ... }[]`)
 * as entradas do recorte oculto, quando o escopo efetivo da consulta é
 * inteiro da lista de corte. Usado nas seções do Dashboard cuja janela é
 * relativa a hoje e não manda `date_from`/`date_to` ao gateway — sem intervalo
 * pra recortar, mas com um `month` por item que dá pra filtrar direto.
 */
export function excludeCutoffMonthlyItems<T>(
  items: T[],
  getMonth: (item: T) => string | null | undefined,
  effectiveGeneratorIds: number[],
): T[] {
  if (!isEntireScopeCutoff(effectiveGeneratorIds)) {
    return items;
  }
  return items.filter((item) => !isMonthInCutoffRange(getMonth(item)));
}

/**
 * Mesma lógica de `clampDateRangeForCutoff`, para arrays de meses 'YYYY-MM'
 * (filtro do Indicadores Ambientais, que não usa um intervalo contínuo).
 */
export function filterCutoffMonths(months: string[], effectiveGeneratorIds: number[]): string[] {
  if (!isEntireScopeCutoff(effectiveGeneratorIds)) {
    return months;
  }

  const filtered = months.filter((month) => !isMonthInCutoffRange(month));
  // Todos os meses pedidos caíam no recorte: devolve um mês-sentinela sem
  // dado real, para não mandar `months: []` (comportamento do backend nesse
  // caso não é garantido).
  return filtered.length > 0 ? filtered : [EMPTY_WINDOW_MONTH];
}

// Reexport interno para uso em testes de fronteira sem duplicar valores mágicos.
export const __internal = {
  AFTER_CUTOFF_DATE,
  AFTER_CUTOFF_MONTH,
  EMPTY_WINDOW_DATE,
  EMPTY_WINDOW_MONTH,
};
